import { API_CONFIG } from '../config/api';
import { toUserFacingPromoError } from './promoMessages';
import { incrementLoading, decrementLoading, setLoadingText } from '../stores/loadingStore';

interface ApiClientOptions {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  loadingText?: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  loadingText?: string;
  skipLoading?: boolean;
}

/**
 * Создает API клиент с автоматическим управлением состоянием загрузки
 */
export class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private defaultLoadingText: string;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl || API_CONFIG.baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...options.defaultHeaders,
    };
    this.defaultLoadingText = options.loadingText || 'Загрузка...';
  }

  /**
   * Выполняет HTTP запрос с автоматическим управлением состоянием загрузки
   */
  async request<T = any>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      loadingText,
      skipLoading = false,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const requestHeaders = { ...this.defaultHeaders, ...headers };

    // Управляем состоянием загрузки
    if (!skipLoading) {
      setLoadingText(loadingText || this.defaultLoadingText);
      incrementLoading();
    }

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401 && errorData.errorCode) {
          console.error('[Telegram auth]', errorData.errorCode);
        }
        if (API_CONFIG.debug || new URLSearchParams(window.location.search).has('debug')) {
          console.error('[API]', endpoint, response.status, errorData);
        }
        const rawMessage =
          errorData.message ||
          errorData.error ||
          (typeof errorData.title === 'string' ? errorData.title : null) ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(
          toUserFacingPromoError(rawMessage, response.status, errorData.errorCode)
        );
      }

      return await response.json();
    } finally {
      if (!skipLoading) {
        decrementLoading();
      }
    }
  }

  /**
   * GET запрос
   */
  get<T = any>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST запрос
   */
  post<T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT запрос
   */
  put<T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE запрос
   */
  delete<T = any>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * PATCH запрос
   */
  patch<T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }
}

/**
 * Создает API клиент для Telegram-аутентифицированных запросов.
 * start_param часто есть только в initDataUnsafe — пробрасываем отдельным заголовком.
 */
export const createTelegramApiClient = (initData: string, startParam?: string | null) => {
  const resolvedStartParam =
    startParam ?? window.Telegram?.WebApp?.initDataUnsafe?.start_param ?? undefined;

  const defaultHeaders: Record<string, string> = {
    'X-Telegram-Init-Data': initData,
  };

  if (resolvedStartParam) {
    defaultHeaders['X-Telegram-Start-Param'] = resolvedStartParam;
  }

  return new ApiClient({
    defaultHeaders,
  });
};

/**
 * Глобальный API клиент для неаутентифицированных запросов
 */
export const apiClient = new ApiClient();
